import React, { useRef, memo } from 'react';
import { AutoSizer, CellMeasurer, CellMeasurerCache, List } from 'react-virtualized';
import PropTypes from 'prop-types';

import ServicesList from '../../../components/List';

import * as importTypes from '../../../reducer/constants';
import { getServicesId } from '../../../helper';
import { getHighlightingText } from '../../../../../../helpers/getters';

const Listing = ({ list, categoryLength, provider, dispatchImportState, search, scrollToCategoryId, scrollToServiceId }) => {
  const listRef = useRef();

  const cache = new CellMeasurerCache({
    defaultHeight: 77,
    minHeight: 77,
    fixedWidth: true,
  });

  const onChangeAssignCategory = (id, value) => {
    dispatchImportState({ type: importTypes.IMPORT_CHANGE_CATEGORY, id, value });
  };

  const onToggleEdit = (id, value) => {
    dispatchImportState({ type: importTypes.IMPORT_TOGGLE_EDIT_CATEGORY, id, value });
  };

  const onChecked = (id, checked) => {
    if (checked) {
      dispatchImportState({ type: importTypes.IMPORT_SERVICE_SELECTED_DELETE, id });
    } else {
      dispatchImportState({ type: importTypes.IMPORT_SERVICE_SELECTED_ADD, id });
    }
  };

  const onCheckedMass = (services, checked) => {
    const servicesId = getServicesId(services);
    if (checked) {
      dispatchImportState({ type: importTypes.IMPORT_SERVICE_SELECTED_ALL_DELETE, list: servicesId });
    } else {
      dispatchImportState({ type: importTypes.IMPORT_SERVICE_SELECTED_ALL_ADD, list: servicesId });
    }
  };

  const onChangeCollapse = (id) => {
    dispatchImportState({ type: importTypes.IMPORT_TOGGLE_COLLAPSE_CATEGORY, id });
  };

  const toggleDescription = (id, categoryIndex, index) => {
    dispatchImportState({ type: importTypes.IMPORT_TOGGLE_INFO_SERVICE, id, categoryIndex });
    listRef?.current?.recomputeRowHeights(index);
    listRef?.current?.forceUpdateGrid();
  };

  const scrollToIndex = () => {
    if (scrollToCategoryId || scrollToServiceId) {
      const findIndex = list.findIndex((item) => {
        if (item.collection === 'category' && scrollToCategoryId) {
          if (Number(item.provider_category_id) === Number(scrollToCategoryId)) {
            return true;
          }
        }
        if (item.collection === 'service' && scrollToServiceId) {
          if (Number(item.id) === Number(scrollToServiceId)) {
            return true;
          }
        }
        return false;
      });

      if (findIndex !== -1) {
        return findIndex;
      }
    }

    if (scrollToCategoryId === 0 || scrollToServiceId === 0) {
      return 0;
    }

    return undefined;
  };

  return (
    <AutoSizer disableHeight>
      {({ width }) => (
        <List
          ref={listRef}
          width={width}
          height={550}
          overscanRowCount={20}
          rowCount={list.length}
          deferredMeasurementCache={cache}
          scrollToIndex={scrollToIndex()}
          rowHeight={cache.rowHeight}
          containerStyle={{ overflow: 'initial' }}
          rowRenderer={({ index, style, key, parent }) => {
            const category = list[index];

            switch (category.collection) {
            case 'category':
              return (
                <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
                  <ServicesList.Category style={style} key={index} active={category.checked}>
                    <ServicesList.Header>
                      <ServicesList.CategoryCheckbox category={category} index={index} onCheckedMass={onCheckedMass} />

                      <ServicesList.CategoryInfo>
                        <div className="services-import__list-category-info-title">
                          <label htmlFor={`select_all_${index}`} className="mb-0">
                            {category.category_title}
                          </label>
                        </div>
                        <ServicesList.CategoryAssign
                          index={index}
                          categoryLength={categoryLength}
                          category={category}
                          onChangeAssignCategory={onChangeAssignCategory}
                          toggleEdit={onToggleEdit}
                        />
                      </ServicesList.CategoryInfo>
                    </ServicesList.Header>
                    <ServicesList.CategoryCollapse onChangeCollapse={() => onChangeCollapse(category.provider_category_id)} category={category} />
                  </ServicesList.Category>
                </CellMeasurer>
              );

            default:
              const { id, name, description, rate, disabled, cancel, refill, dripfeed, provider_rate, checked, showInfo, categoryIndex } = category;
              const nameWithId = `${id} - ${name}`;

              return (
                <CellMeasurer key={key} cache={cache} parent={parent} columnIndex={0} rowIndex={index}>
                  <ServicesList.Row style={style} active={checked} disabled={disabled} key={id}>
                    <ServicesList.ServiceCol>
                      <ServicesList.Checkbox id={`import_${id}`} checked={checked} disabled={disabled} onChange={() => onChecked(id, checked)} />
                      <ServicesList.ServiceInfo>
                        <ServicesList.ServiceData>
                          <ServicesList.Name>
                            <label htmlFor={`import_${id}`}>
                              <span dangerouslySetInnerHTML={getHighlightingText(nameWithId, search)} />
                            </label>
                          </ServicesList.Name>
                          <ServicesList.Rate provider={provider} providerRate={provider_rate} rate={rate} />
                        </ServicesList.ServiceData>
                        <ServicesList.ServiceFooter
                          options={{ cancel, refill, dripfeed }}
                          toggleDescription={() => toggleDescription(id, categoryIndex, index)}
                        />
                        {showInfo && <ServicesList.ServiceDescription description={description} />}
                      </ServicesList.ServiceInfo>
                    </ServicesList.ServiceCol>
                  </ServicesList.Row>
                </CellMeasurer>
              );
            }
          }}
        />
      )}
    </AutoSizer>
  );
};

Listing.propTypes = {
  list: PropTypes.array,
  categoryLength: PropTypes.number,
  provider: PropTypes.object,
  dispatchImportState: PropTypes.func,
  search: PropTypes.string,
  scrollToCategoryId: PropTypes.number,
  scrollToServiceId: PropTypes.number,
};

const arePropsEqual = (prevProps, nextProps) => nextProps.isLoading;
export default memo(Listing, arePropsEqual);
